import type {createSafeIndexedDbPersister} from '@github-ui/safe-indexed-db-storage'
import type {Sink} from 'relay-runtime/lib/network/RelayObservable'

type Storage = ReturnType<typeof createSafeIndexedDbPersister>

interface StoragePersisterOptions {
  /** The storage client used for setting and retrieving items from cache.
   * For SSR pass in `undefined`.
   */
  storage: Storage | undefined | null
  /**
   * The max-allowed age of the cache in milliseconds.
   * If a persisted cache is found that is older than this
   * time, it will be discarded
   */
  maxAgeMs: number
  /**
   * Prefix to be used for storage key.
   * Storage key is a combination of prefix and query hash in a form of `prefix-queryHash`.
   */
  prefix: string
}
type CacheKey = string

/**
 * Generates a cache key from query ID and variables
 */
function generateCacheKey(prefix: string, queryId: string, variables: Record<string, unknown>): CacheKey {
  return `${prefix}-${queryId}#${JSON.stringify(variables)}`
}

export type QueryCache<T> = ReturnType<typeof createQueryCache<T>>

export function createQueryCache<T>({storage, prefix, maxAgeMs}: StoragePersisterOptions) {
  async function retrieveQuery(
    queryId: string,
    variables: Record<string, unknown>,
    attributes?: {[key: string]: unknown},
  ): Promise<T | undefined> {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryId, variables)
    try {
      const persistedQuery = await storage.getItem(cacheKey, attributes)
      return persistedQuery?.state.data as T | undefined
    } catch {
      return undefined
    }
  }

  async function tryCacheFetchResult(
    queryId: string,
    variables: Record<string, unknown>,
    fetchResult: T | null,
    ttl: number,
    attributes?: {[key: string]: unknown},
  ) {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryId, variables)
    try {
      await storage.setItem(
        cacheKey,
        {
          queryHash: queryId,
          queryKey: Object.entries(variables).flat(),
          state: {data: fetchResult, dataUpdatedAt: Date.now(), ttl},
        },
        undefined,
        undefined,
        attributes,
      )
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`Failed to persist query for key: ${cacheKey}`)
    }
  }

  async function persisterFn(
    queryFn: () => Promise<T>,
    queryId: string,
    variables: Record<string, unknown>,
    observer: Sink<T>,
    ttl?: number,
    attributes?: {[key: string]: unknown},
  ): Promise<T> {
    const cachedResult = await retrieveQuery(queryId, variables, attributes)

    let cachedResultString: string | null = null
    let queryResultString: string | null = null
    if (cachedResult) {
      if (typeof cachedResult === 'object' && cachedResult !== null && 'data' in cachedResult) {
        cachedResultString = JSON.stringify((cachedResult as {[key: string]: unknown}).data)
      }
      observer.next(cachedResult)
    }

    const fetchResult = await queryFn()

    if (fetchResult && storage) {
      if (typeof fetchResult === 'object' && fetchResult !== null && 'data' in fetchResult) {
        queryResultString = JSON.stringify((fetchResult as {[key: string]: unknown}).data)
      }
      const updateType = !cachedResult ? 'new' : cachedResultString !== queryResultString ? 'changed' : 'unchanged'
      tryCacheFetchResult(queryId, variables, fetchResult, ttl ?? maxAgeMs, {...attributes, update_type: updateType})
    }

    return fetchResult
  }

  return {persisterFn, retrieveQuery}
}
